import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Button, Input, Select, Tag, Space, Modal,
  Form, notification, Switch, Row, Col,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,
  BookOutlined, SearchOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../config';

const { Option } = Select;
const { TextArea } = Input;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

const CATEGORIES = ['general', 'history', 'tradition', 'culture', 'religion', 'governance'];

export default function SamajParichayList() {
  const [data,     setData]     = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [modal,    setModal]    = useState(null); // null | 'add' | 'edit'
  const [editing,  setEditing]  = useState(null);
  const [saving,   setSaving]   = useState(false);
  const [search,   setSearch]   = useState('');
  const [form] = Form.useForm();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${baseUrl}/admin/samaj-parichay`, { headers: auth() });
      if (r.data.status) setData(r.data.result || []);
    } catch { notification.error({ message: 'Failed to load', placement: 'topRight' }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openAdd = () => { form.resetFields(); setEditing(null); setModal('edit'); };
  const openEdit = (row) => { form.setFieldsValue({ ...row, is_published: !!row.is_published }); setEditing(row); setModal('edit'); };

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (editing) {
        await axios.patch(`${baseUrl}/admin/samaj-parichay/${editing.id}`, values, { headers: auth() });
        notification.success({ message: 'Updated', placement: 'topRight' });
      } else {
        await axios.post(`${baseUrl}/admin/samaj-parichay`, values, { headers: auth() });
        notification.success({ message: 'Created', placement: 'topRight' });
      }
      setModal(null); fetchData();
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || 'Error', placement: 'topRight' });
    } finally { setSaving(false); }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Remove this entry?', okType: 'danger', okText: 'Remove',
      onOk: async () => {
        await axios.delete(`${baseUrl}/admin/samaj-parichay/${id}`, { headers: auth() });
        fetchData();
      },
    });
  };

  const filtered = data.filter(d =>
    !search || d.title?.toLowerCase().includes(search.toLowerCase()) ||
    d.category?.toLowerCase().includes(search.toLowerCase())
  );

  const columns = [
    { title: '#', dataIndex: 'id', width: 55 },
    { title: 'Title (Hindi)', dataIndex: 'title', ellipsis: true },
    { title: 'Title (EN)', dataIndex: 'title_en', ellipsis: true, render: v => v || '—' },
    { title: 'Category', dataIndex: 'category', width: 110, render: v => <Tag>{v}</Tag> },
    { title: 'Type', dataIndex: 'type', width: 85, render: v => <Tag color={v === 'qa' ? 'blue' : 'green'}>{v}</Tag> },
    { title: 'Published', dataIndex: 'is_published', width: 90, render: v => v ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag> },
    { title: 'Order', dataIndex: 'sort_order', width: 70 },
    {
      title: 'Actions', width: 110,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(r.id)} />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '12px 0' }}>
      <Card
        title={<><BookOutlined style={{ color: '#7c3aed', marginRight: 8 }} />Samaj Parichay — Community Knowledge</>}
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchData} />
            <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>Add Entry</Button>
          </Space>
        }
      >
        <div style={{ marginBottom: 14 }}>
          <Input prefix={<SearchOutlined />} placeholder="Search title or category..." value={search}
            onChange={e => setSearch(e.target.value)} allowClear style={{ width: 300 }} />
        </div>
        <Table dataSource={filtered} columns={columns} rowKey="id" loading={loading} size="small" />
      </Card>

      <Modal
        open={modal === 'edit'}
        title={editing ? 'Edit Entry' : 'Add Entry'}
        onCancel={() => setModal(null)}
        onOk={handleSave}
        confirmLoading={saving}
        width={640}
        okText={editing ? 'Update' : 'Create'}
      >
        <Form form={form} layout="vertical" initialValues={{ type: 'article', category: 'general', sort_order: 0, is_published: true }}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="category" label="Category" rules={[{ required: true }]}>
                <Select>{CATEGORIES.map(c => <Option key={c} value={c}>{c}</Option>)}</Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="type" label="Type" rules={[{ required: true }]}>
                <Select>
                  <Option value="article">Article</Option>
                  <Option value="qa">Q&A</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="title" label="Title (Hindi)" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="title_en" label="Title (English)">
            <Input />
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(p, c) => p.type !== c.type}>
            {({ getFieldValue }) => getFieldValue('type') === 'qa' && (
              <>
                <Form.Item name="question" label="Question (Hindi)">
                  <TextArea rows={2} />
                </Form.Item>
                <Form.Item name="question_en" label="Question (English)">
                  <TextArea rows={2} />
                </Form.Item>
              </>
            )}
          </Form.Item>
          <Form.Item name="content" label="Content / Answer (Hindi)">
            <TextArea rows={4} />
          </Form.Item>
          <Form.Item name="content_en" label="Content / Answer (English)">
            <TextArea rows={4} />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="sort_order" label="Sort Order">
                <Input type="number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="is_published" label="Published" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
}
